import { AGENT_PORTAL_DASH_URL, PRODUCER_SESSION_DETAILS_URL, EMPTY_PRODUCER_SESSION } from './producer-constants';

export interface ProducerSessionDetails {
    firstName?: string;
    lastName?: string;
    fullName?: string;
    email?: string;
    phone?: string;
    phoneNumber?: string;
    workPhoneNumber?: string;
    producerNumber?: string;
    pNumber?: string;
    userID?: string;
}

type AgentBannerDetails = {
    fullName: string;
    producerNumber: string;
    phoneNumber: string;
    email: string;
};

const getTextContent = (doc: Document, selector: string): string =>
    doc
        .querySelector(selector)
        ?.textContent?.replace(/\u00a0/g, ' ')
        .trim() || '';

const getBannerField = (doc: Document, html: string, className: string): string => {
    const selectorValue = getTextContent(doc, `.${className}`);
    if (selectorValue) {
        return selectorValue;
    }

    const regex = new RegExp(`<[^>]+class=["'][^"']*${className}[^"']*["'][^>]*>\\s*([^<]+?)\\s*<\\/[^>]+>`, 'i');
    return (
        html
            .match(regex)?.[1]
            ?.replace(/\u00a0/g, ' ')
            .trim() || ''
    );
};

const parseAgentBannerDetails = (doc: Document, html: string): AgentBannerDetails | null => {
    const fullName = getBannerField(doc, html, 'agent-name');
    const producerNumber = getBannerField(doc, html, 'agent-id');
    const phoneNumber = getBannerField(doc, html, 'agent-phone');
    const email = getBannerField(doc, html, 'agent-email');

    if (!fullName && !producerNumber && !phoneNumber && !email) {
        return null;
    }

    return {
        fullName,
        producerNumber,
        phoneNumber,
        email,
    };
};

export const fetchAgentBannerFromRateQuotePage = async (): Promise<AgentBannerDetails | null> => {
    if (!AGENT_PORTAL_DASH_URL) {
        return null;
    }

    const url = new URL(AGENT_PORTAL_DASH_URL, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.forEach((value, key) => {
        url.searchParams.set(key, value);
    });

    const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: {
            Accept: 'text/html',
        },
    });

    if (!response.ok) {
        throw new Error(`Rate quote page request failed with ${response.status}`);
    }

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return parseAgentBannerDetails(doc, html);
};

const bannerDetailsToProducerSession = (bannerDetails: AgentBannerDetails): ProducerSessionDetails => {
    const normalizedFullName = bannerDetails.fullName.replace(/\s+/g, ' ').trim();
    const nameParts = normalizedFullName ? normalizedFullName.split(/\s+/) : [];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ');
    const producerNumber = bannerDetails.producerNumber.trim();
    const phoneNumber = bannerDetails.phoneNumber.trim();
    const email = bannerDetails.email.trim();

    return normalizeProducerSession({
        ...EMPTY_PRODUCER_SESSION,
        fullName: normalizedFullName,
        firstName,
        lastName,
        producerNumber,
        pNumber: producerNumber,
        userID: producerNumber,
        email,
        phone: phoneNumber,
        phoneNumber,
        workPhoneNumber: phoneNumber,
    });
};

/**
 * Fetch producer session details from the server
 * Returns producer info if available, otherwise returns empty session
 */
export const fetchProducerSessionDetails = async (): Promise<ProducerSessionDetails> => {
    try {
        const response = await fetch(PRODUCER_SESSION_DETAILS_URL, {
            method: 'POST',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
            },
        });

        if (!response.ok) {
            console.warn(`Producer session request failed with ${response.status}`);
            return EMPTY_PRODUCER_SESSION;
        }

        const session = (await response.json()) as ProducerSessionDetails;
        return normalizeProducerSession(session);
    } catch (error) {
        console.warn('Unable to load producer session details:', error);
        return EMPTY_PRODUCER_SESSION;
    }
};

/**
 * Fetch agent details for banner display, preferring rate quote page HTML banner values,
 * then falling back to producer session endpoint.
 */
export const fetchAgentDetails = async (): Promise<ProducerSessionDetails> => {
    try {
        const bannerDetails = await fetchAgentBannerFromRateQuotePage();
        if (bannerDetails) {
            return bannerDetailsToProducerSession(bannerDetails);
        }
    } catch (error) {
        console.warn('Unable to load agent banner details:', error);
    }

    return fetchProducerSessionDetails();
};

/**
 * Normalize producer session details to ensure all fields are present
 */
export const normalizeProducerSession = (session: ProducerSessionDetails): ProducerSessionDetails => {
    if (!session) {
        return EMPTY_PRODUCER_SESSION;
    }

    const producerNumber = session.producerNumber || session.pNumber || session.userID || '';
    const phoneNumber = session.workPhoneNumber || session.phoneNumber || session.phone || '';

    return {
        ...session,
        producerNumber,
        pNumber: producerNumber,
        phone: session.phone || phoneNumber,
        phoneNumber,
        workPhoneNumber: session.workPhoneNumber || '',
        fullName: session.fullName || `${session.firstName || ''} ${session.lastName || ''}`.trim(),
    };
};

/**
 * Check if producer session has valid data
 */
export const hasProducerSession = (session: ProducerSessionDetails): boolean => {
    return !!(
        session &&
        (session.firstName ||
            session.lastName ||
            session.email ||
            session.phone ||
            session.producerNumber ||
            session.userID)
    );
};
